import { STARTER_BOARDS } from "@kourai/api/home-board";
import { Globe, Loader2, Mail, Pencil, Plus, RotateCw, Trash2, UserMinus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/atoms/avatar";
import { useWorkspace, type WorkspaceInfo } from "@/hooks/use-workspace";
import { useApi } from "@/lib/api-client";
import { authClient, useSession } from "@/lib/auth-client";
import { RoleSelect } from "./role-select";

interface Member {
  id: string;
  userId: string;
  role: string;
  user: { id: string; name: string; email: string; image?: string | null };
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
}

function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function deriveLogoUrl(url: string): string | null {
  if (!url.trim()) return null;
  try {
    const hostname = new URL(url.startsWith("http") ? url : `https://${url}`).hostname;
    return `https://img.logo.dev/${hostname}`;
  } catch {
    return null;
  }
}

function getPageUrl(metadata: string | null | undefined): string {
  if (!metadata) return "";
  try {
    return (JSON.parse(metadata) as { pageUrl?: string })?.pageUrl ?? "";
  } catch {
    return "";
  }
}

function WorkspaceCard({
  workspace,
  isActive,
  members,
  invitations,
  isExpanded,
  onToggleExpand,
  onDelete,
  onSwitchWorkspace,
  onInvite,
  onCancelInvite,
  onRemoveMember,
  onSaveEdit,
  currentUserId,
}: {
  workspace: WorkspaceInfo;
  isActive: boolean;
  members: Member[];
  invitations: Invitation[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDelete: (id: string) => void;
  onSwitchWorkspace: (id: string) => void;
  onInvite: (workspaceId: string, email: string, role: string, resend?: boolean) => void;
  onCancelInvite: (workspaceId: string, invitationId: string) => void;
  onRemoveMember: (workspaceId: string, memberId: string) => void;
  onSaveEdit: (
    id: string,
    name: string,
    slug: string,
    logo: string | null,
    metadata: Record<string, unknown> | undefined,
  ) => void;
  currentUserId: string;
}) {
  const userRole = members.find((m) => m.userId === currentUserId)?.role ?? "";
  const isPrivate = workspace.name === "Private";
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(workspace.name);
  const [editPageUrl, setEditPageUrl] = useState(getPageUrl(workspace.metadata));
  const [logoPreview, setLogoPreview] = useState<string | null>(workspace.logo);
  const [logoLoading, setLogoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePageUrlChange = useCallback((value: string) => {
    setEditPageUrl(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!value.trim()) {
      setLogoPreview(null);
      return;
    }
    setLogoLoading(true);
    timerRef.current = setTimeout(() => {
      const url = deriveLogoUrl(value);
      setLogoPreview(url);
      setLogoLoading(false);
    }, 800);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  async function handleSave() {
    if (!editName.trim()) return;
    setSaving(true);
    const slug = deriveSlug(editName);
    const metadata = editPageUrl.trim() ? { pageUrl: editPageUrl.trim() } : undefined;
    await onSaveEdit(workspace.id, editName.trim(), slug, logoPreview, metadata as never);
    setEditing(false);
    setSaving(false);
  }

  const pageUrl = getPageUrl(workspace.metadata);
  const workspaceLogo = workspace.logo;

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar size="sm">
            <AvatarImage src={workspaceLogo ?? undefined} alt={workspace.name} />
            <AvatarFallback>{workspace.name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-medium truncate max-w-full">{workspace.name}</span>
              {isPrivate && (
                <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded shrink-0">
                  Private
                </span>
              )}
              <span className="text-xs text-muted-foreground capitalize bg-muted px-1.5 py-0.5 rounded shrink-0">
                {userRole}
              </span>
              {isActive && (
                <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded shrink-0 flex items-center gap-1">
                  <span className="size-1.5 rounded-full bg-green-500" />
                  Active
                </span>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {members.length} {members.length === 1 ? "member" : "members"}
            </span>
            {pageUrl && (
              <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                <Globe className="size-3 shrink-0" />
                <span className="truncate">{pageUrl}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-2">
          {!isActive && (
            <button
              type="button"
              onClick={() => onSwitchWorkspace(workspace.id)}
              className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-accent transition-colors"
            >
              Make active
            </button>
          )}
          {!isPrivate && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              {isExpanded ? "Close" : "Manage"}
            </button>
          )}
          {!isPrivate && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <DeleteWorkspaceConfirm
          workspaceName={workspace.name}
          onConfirm={() => {
            onDelete(workspace.id);
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {isExpanded && (
        <div className="space-y-4 pt-3 border-t">
          <button
            type="button"
            onClick={() => {
              setEditName(workspace.name);
              setEditPageUrl(pageUrl);
              setLogoPreview(workspaceLogo);
              setEditing(!editing);
            }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="size-3.5" />
            {editing ? "Cancel edit" : "Edit workspace"}
          </button>

          {editing && (
            <div className="space-y-3">
              <div className="space-y-2">
                <label htmlFor={`edit-name-${workspace.id}`} className="text-xs font-medium">
                  Name
                </label>
                <input
                  id={`edit-name-${workspace.id}`}
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor={`edit-url-${workspace.id}`} className="text-xs font-medium">
                  Page URL <span className="text-muted-foreground">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id={`edit-url-${workspace.id}`}
                    type="url"
                    value={editPageUrl}
                    onChange={(e) => handlePageUrlChange(e.target.value)}
                    placeholder="https://acme.com"
                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {logoLoading && (
                    <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
                  )}
                </div>
                {logoPreview && !logoLoading && (
                  <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-2">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="size-10 rounded object-contain bg-white"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span className="text-xs text-muted-foreground truncate">{logoPreview}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !editName.trim()}
                  className="rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Members ({members.length})
            </p>
            <div className="space-y-1">
              {members.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 py-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm" title={m.user.email}>
                      {m.user.name || m.user.email}
                    </span>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground capitalize">
                      {m.role}
                    </span>
                  </div>
                  {m.role !== "owner" && userRole === "owner" && (
                    <button
                      type="button"
                      onClick={() => onRemoveMember(workspace.id, m.id)}
                      title="Remove from workspace"
                      aria-label={`Remove ${m.user.name || m.user.email}`}
                      className="shrink-0 rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <UserMinus className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Pending invitations ({invitations.filter((inv) => inv.status === "pending").length})
            </p>
            <div className="space-y-1">
              {invitations
                .filter((inv) => inv.status === "pending")
                .map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="size-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate">{inv.email}</span>
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground capitalize">
                        {inv.role}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <button
                        type="button"
                        title="Resend the invite (same link, another 48 hours)"
                        onClick={() => onInvite(workspace.id, inv.email, inv.role, true)}
                        className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <RotateCw className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        title="Cancel the invite (its link stops working)"
                        onClick={() => onCancelInvite(workspace.id, inv.id)}
                        className="rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              {invitations.filter((inv) => inv.status === "pending").length === 0 && (
                <p className="text-xs text-muted-foreground">No pending invitations</p>
              )}
            </div>
          </div>

          {userRole === "owner" && !isPrivate && (
            <InviteForm workspaceId={workspace.id} onInvite={onInvite} />
          )}
        </div>
      )}
    </div>
  );
}

/** Better Auth's invite errors, said the way maquette's share dialog says them. */
function inviteErrorMessage(error: { code?: string; message?: string }, email: string) {
  switch (error.code) {
    case "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION":
      return `${email} is already in this workspace.`;
    case "INVITATION_LIMIT_REACHED":
      return "This workspace has too many pending invites. Cancel some first.";
    case "ORGANIZATION_MEMBERSHIP_LIMIT_REACHED":
      return "This workspace is full.";
    default:
      return error.message ?? "Couldn't send the invite.";
  }
}

function InviteForm({
  workspaceId,
  onInvite,
}: {
  workspaceId: string;
  onInvite: (workspaceId: string, email: string, role: string, resend?: boolean) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");

  const send = () => {
    if (!email.trim()) return;
    onInvite(workspaceId, email.trim(), role);
    setEmail("");
  };

  // A form, so Enter in the address field sends the invite.
  return (
    <form
      className="space-y-1 pt-2 border-t"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <label htmlFor={`invite-email-${workspaceId}`} className="text-xs font-medium">
        Invite by email
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={`invite-email-${workspaceId}`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@example.com"
          className="h-8 min-w-0 flex-[1_1_8rem] rounded-lg border border-border bg-background px-2.5 text-xs outline-none focus:ring-2 focus:ring-primary/30"
        />
        <RoleSelect value={role} onValueChange={setRole} />
        <button
          type="submit"
          disabled={!email.trim()}
          className="h-8 shrink-0 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          Invite
        </button>
      </div>
    </form>
  );
}

export function WorkspaceSettings() {
  const {
    workspaces,
    activeOrganizationId,
    switchWorkspace,
    refetch: refetchWorkspaces,
  } = useWorkspace();
  const { data: session } = useSession();
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createPageUrl, setCreatePageUrl] = useState("");
  const [createLogoPreview, setCreateLogoPreview] = useState<string | null>(null);
  const [createLogoLoading, setCreateLogoLoading] = useState(false);
  /** The home page new members start from (ADMIN-264): a built-in starter, or "" for Recent. */
  const [createStarter, setCreateStarter] = useState("");
  const api = useApi();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [invitations, setInvitations] = useState<Record<string, Invitation[]>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const createTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadWorkspaceDetails = useCallback(async (workspaceId: string) => {
    try {
      const result = await authClient.organization.getFullOrganization({
        query: { organizationId: workspaceId },
      });
      if (!result.error && result.data) {
        const data = result.data as { members: Member[]; invitations: Invitation[] };
        setMembers((prev) => ({ ...prev, [workspaceId]: data.members ?? [] }));
        setInvitations((prev) => ({ ...prev, [workspaceId]: data.invitations ?? [] }));
      }
    } catch (err) {
      console.error(
        "[workspace] Failed to load workspace details:",
        err instanceof Error ? err.message : err,
      );
    }
  }, []);

  useEffect(() => {
    for (const ws of workspaces) {
      loadWorkspaceDetails(ws.id);
    }
  }, [workspaces, loadWorkspaceDetails]);

  async function handleCreate() {
    if (!createName.trim()) return;
    setError("");
    try {
      const slug = deriveSlug(createName);
      const logo = deriveLogoUrl(createPageUrl);
      const metadata: Record<string, unknown> | undefined = createPageUrl.trim()
        ? { pageUrl: createPageUrl.trim() }
        : undefined;
      const result = await authClient.organization.create({
        name: createName.trim(),
        slug,
        logo: logo ?? undefined,
        metadata,
      } as Parameters<typeof authClient.organization.create>[0]);
      if (result.error) {
        setError((result.error as { message?: string })?.message ?? "Failed to create workspace");
        return;
      }
      const starter = STARTER_BOARDS.find((s) => s.id === createStarter);
      const created = result.data as { id?: string } | null;
      if (starter && created?.id)
        await api.api.workspaces[":id"].home.starter
          .$put({ param: { id: created.id }, json: { board: starter.board } })
          .catch(() => {});
      setCreateName("");
      setCreatePageUrl("");
      setCreateStarter("");
      setCreateLogoPreview(null);
      setCreating(false);
      refetchWorkspaces();
    } catch (err) {
      console.error(
        "[workspace] Failed to create workspace:",
        err instanceof Error ? err.message : err,
      );
      setError("Network error");
    }
  }

  async function handleSaveEdit(
    id: string,
    name: string,
    slug: string,
    logo: string | null,
    metadata: Record<string, unknown> | undefined,
  ) {
    try {
      await authClient.organization.update({
        organizationId: id,
        data: { name, slug, logo: logo ?? undefined, metadata },
      } as Parameters<typeof authClient.organization.update>[0]);
      refetchWorkspaces();
    } catch (err) {
      console.error(
        "[workspace] Failed to update workspace:",
        err instanceof Error ? err.message : err,
      );
    }
  }

  async function handleDelete(id: string) {
    try {
      await authClient.organization.delete({ organizationId: id });
      refetchWorkspaces();
      if (activeOrganizationId === id) {
        const first = workspaces.filter((w) => w.id !== id)[0];
        if (first) switchWorkspace(first.id);
      }
    } catch (err) {
      console.error(
        "[workspace] Failed to delete workspace:",
        err instanceof Error ? err.message : err,
      );
    }
  }

  async function handleInvite(workspaceId: string, email: string, role: string, resend = false) {
    setError("");
    setNotice("");
    try {
      const result = await authClient.organization.inviteMember({
        organizationId: workspaceId,
        email,
        role: role as "member" | "admin",
        resend,
      });
      if (result.error) {
        setError(inviteErrorMessage(result.error, email));
        return;
      }
      setNotice(resend ? `Invite sent again to ${email}.` : `Invite sent to ${email}.`);
      loadWorkspaceDetails(workspaceId);
    } catch {
      setError("Network error");
    }
  }

  async function handleCancelInvite(workspaceId: string, invitationId: string) {
    try {
      const result = await authClient.organization.cancelInvitation({ invitationId });
      if (!result.error) {
        loadWorkspaceDetails(workspaceId);
      } else {
        console.error(
          "[workspace] Failed to cancel invitation:",
          (result.error as { message?: string })?.message,
        );
      }
    } catch (err) {
      console.error(
        "[workspace] Failed to cancel invitation:",
        err instanceof Error ? err.message : err,
      );
    }
  }

  async function handleRemoveMember(workspaceId: string, memberId: string) {
    try {
      const result = await authClient.organization.removeMember({
        memberIdOrEmail: memberId,
        organizationId: workspaceId,
      });
      if (!result.error) {
        loadWorkspaceDetails(workspaceId);
      }
    } catch (err) {
      console.error(
        "[workspace] Failed to remove member:",
        err instanceof Error ? err.message : err,
      );
    }
  }

  function handleCreatePageUrlChange(value: string) {
    setCreatePageUrl(value);
    if (createTimerRef.current) clearTimeout(createTimerRef.current);
    if (!value.trim()) {
      setCreateLogoPreview(null);
      return;
    }
    setCreateLogoLoading(true);
    createTimerRef.current = setTimeout(() => {
      const url = deriveLogoUrl(value);
      setCreateLogoPreview(url);
      setCreateLogoLoading(false);
    }, 800);
  }

  useEffect(() => {
    return () => {
      if (createTimerRef.current) clearTimeout(createTimerRef.current);
    };
  }, []);

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <p className="text-sm text-muted-foreground">
        Workspaces help you collaborate with others. Create shared spaces for chat and providers.
      </p>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {notice && !error && (
        <div role="status" className="rounded-md bg-primary/10 px-4 py-2 text-sm text-primary">
          {notice}
        </div>
      )}

      {workspaces.map((ws) => (
        <WorkspaceCard
          key={ws.id}
          workspace={ws}
          isActive={ws.id === activeOrganizationId}
          members={members[ws.id] ?? []}
          invitations={invitations[ws.id] ?? []}
          isExpanded={expanded === ws.id}
          onToggleExpand={() => setExpanded(expanded === ws.id ? null : ws.id)}
          onDelete={handleDelete}
          onSwitchWorkspace={switchWorkspace}
          onInvite={handleInvite}
          onCancelInvite={handleCancelInvite}
          onRemoveMember={handleRemoveMember}
          onSaveEdit={handleSaveEdit}
          currentUserId={session?.user?.id ?? ""}
        />
      ))}

      {creating ? (
        <div className="rounded-lg border p-4 space-y-3">
          <p className="text-sm font-medium">Create workspace</p>
          <div className="space-y-2">
            <label htmlFor="create-name" className="text-xs font-medium">
              Name
            </label>
            <input
              id="create-name"
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="Workspace name"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            />
            <label htmlFor="create-url" className="text-xs font-medium">
              Page URL <span className="text-muted-foreground">(optional)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                id="create-url"
                type="url"
                value={createPageUrl}
                onChange={(e) => handleCreatePageUrlChange(e.target.value)}
                placeholder="https://acme.com"
                className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
              {createLogoLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
              )}
            </div>
            {createLogoPreview && !createLogoLoading && (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-2">
                <img
                  src={createLogoPreview}
                  alt="Logo preview"
                  className="size-10 rounded object-contain bg-white"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <span className="text-xs text-muted-foreground truncate">{createLogoPreview}</span>
              </div>
            )}
            <label htmlFor="create-starter" className="text-xs font-medium">
              Home page for new members
            </label>
            <select
              id="create-starter"
              value={createStarter}
              onChange={(e) => setCreateStarter(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Recent only, like a new account</option>
              {STARTER_BOARDS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}: {s.description.toLowerCase()}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Everyone can still arrange their own. You can change it later from Customize.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!createName.trim()}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
        >
          <Plus className="size-4" />
          Create workspace
        </button>
      )}
    </div>
  );
}

function DeleteWorkspaceConfirm({
  workspaceName,
  onConfirm,
  onCancel,
}: {
  workspaceName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
      <p className="text-sm font-medium text-destructive">Delete workspace?</p>
      <p className="text-sm text-muted-foreground">
        All shared chats and provider configs in <strong>{workspaceName}</strong> will be
        permanently lost. This action cannot be undone.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90 transition-colors"
        >
          Delete workspace
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
